import React, { useState, useEffect, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faClockRotateLeft,
  faClock,
  faStickyNote,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../../contexts/UserContext";
import apiService from "../../../ApiService";
import MarkdownRenderer from "./MarkdownRenderer";
import "./StudyDashboard.css";

const StudyDashboard = ({ onViewSubject }) => {
  const { user } = useContext(UserContext);
  const userId = user?.userId;
  const [statistics, setStatistics] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch subjects first to get the count
      const subjectsData = await apiService.getSubjects(userId);
      const allSubjects = subjectsData.subjects || [];
      console.log(
        "Full subject object:",
        JSON.stringify(allSubjects[0], null, 2),
      );
      console.log("Subject keys:", Object.keys(allSubjects[0] || {}));
      setSubjects(allSubjects.slice(0, 6));

      // Fetch study statistics
      const statsResponse = await apiService.getStudyStatistics(userId);

      // Extract statistics from nested response
      const stats = statsResponse.statistics || {};

      // Fetch all notes to get the count
      let notesCount = 0;
      try {
        const notesData = await apiService.getNotes(userId);
        notesCount = notesData.notes?.length || 0;
      } catch (error) {
        console.error("Error fetching notes count:", error);
      }

      // Map snake_case API response to camelCase and calculate totals
      const mappedStats = {
        totalSubjects: allSubjects.length,
        totalSessions: stats.total_sessions || 0,
        totalMinutes: parseInt(stats.total_minutes) || 0,
        avgMinutes: parseFloat(stats.avg_minutes) || 0,
        firstStudyDate: stats.first_study_date || null,
        lastStudyDate: stats.last_study_date || null,
        totalNotes: notesCount,
      };

      setStatistics(mappedStats);

      // Fetch recent study logs
      const logsData = await apiService.getStudyLogs(userId, {
        limit: 5,
        sortBy: "study_date",
        sortOrder: "desc",
      });
      setRecentLogs(logsData.study_logs || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="study-dashboard">
      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card purple">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faBook} />
          </div>
          <div className="stat-content">
            <h3>{statistics?.totalSubjects || 0}</h3>
            <p>Active Subjects</p>
          </div>
        </div>

        <div className="stat-card blue">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </div>
          <div className="stat-content">
            <h3>{statistics?.totalSessions || 0}</h3>
            <p>Study Sessions</p>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="stat-content">
            <h3>{formatDuration(statistics?.totalMinutes || 0)}</h3>
            <p>Total Time</p>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faStickyNote} />
          </div>
          <div className="stat-content">
            <h3>{statistics?.totalNotes || 0}</h3>
            <p>Notes Created</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Recent Subjects */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>
              <FontAwesomeIcon icon={faBook} /> Your Subjects
            </h2>
          </div>
          <div className="subjects-list">
            {subjects.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faBook} />
                <p>No subjects yet. Create your first subject!</p>
              </div>
            ) : (
              subjects.map((subject) => (
                <div
                  key={subject.subject_id}
                  className="subject-item"
                  onClick={() =>
                    onViewSubject && onViewSubject(subject.subject_id)
                  }
                  style={{ cursor: onViewSubject ? "pointer" : "default" }}
                >
                  <div className="subject-info">
                    <h4>{subject.subject_name}</h4>
                    {subject.description && (
                      <MarkdownRenderer
                        content={subject.description}
                        className="subject-desc"
                        preview
                        previewLength={150}
                      />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Study Logs */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>
              <FontAwesomeIcon icon={faChartBar} /> Recent Activity
            </h2>
          </div>
          <div className="logs-list">
            {recentLogs.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faClockRotateLeft} />
                <p>No study sessions logged yet.</p>
              </div>
            ) : (
              recentLogs.map((log) => (
                <div key={log.log_id} className="log-item">
                  <div className="log-date">
                    <span className="date-text">
                      {formatDate(log.study_date)}
                    </span>
                  </div>
                  <div className="log-details">
                    <h4>{log.subject_name}</h4>
                    {(log.source_title || log.source_name) && (
                      <p className="log-source">
                        {log.source_title || log.source_name}
                      </p>
                    )}
                  </div>
                  <div className="log-duration">
                    <FontAwesomeIcon icon={faClock} />
                    <span>{formatDuration(log.duration_minutes)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Study Period Summary */}
      {statistics && statistics.firstStudyDate && statistics.lastStudyDate && (
        <div className="stats-chart-section">
          <div className="section-header">
            <h2>
              <FontAwesomeIcon icon={faChartBar} /> Study Summary
            </h2>
          </div>
          <div className="study-summary">
            <div key="first-study" className="summary-item">
              <label>First Study Session</label>
              <p>{formatDate(statistics.firstStudyDate)}</p>
            </div>
            <div key="last-study" className="summary-item">
              <label>Latest Study Session</label>
              <p>{formatDate(statistics.lastStudyDate)}</p>
            </div>
            <div key="avg-duration" className="summary-item">
              <label>Average Session Duration</label>
              <p>{formatDuration(Math.round(statistics.avgMinutes))}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyDashboard;
